import { Card } from './Card.js';
import { Deck } from './Deck.js';
import { GameRules } from './GameRules.js';
import { History } from './History.js';
import { DrawMode, PileType, Move, GameStateData } from '../types/index.js';

export class GameState {
  private stock: Card[] = [];
  private waste: Card[] = [];
  private foundations: Card[][] = [[], [], [], []];
  private tableau: Card[][] = [[], [], [], [], [], [], []];
  private drawMode: DrawMode = DrawMode.THREE_CARD;
  private solvableOnly: boolean = false;
  private history: History = new History();
  private startTime: number = Date.now();

  constructor(drawMode: DrawMode = DrawMode.THREE_CARD, solvableOnly: boolean = false) {
    this.drawMode = drawMode;
    this.solvableOnly = solvableOnly;
  }

  /**
   * Start a new game
   */
  newGame(): void {
    const { tableau, stock } = Deck.dealKlondike({
      solvableOnly: this.solvableOnly,
      drawMode: this.drawMode
    });

    this.stock = stock;
    this.waste = [];
    this.foundations = [[], [], [], []];
    this.tableau = tableau;
    this.history.clear();
    this.startTime = Date.now();
  }

  /**
   * Draw cards from stock to waste
   */
  drawFromStock(): boolean {
    if (this.stock.length === 0) {
      // If stock is empty, reset from waste
      if (this.waste.length === 0) {
        return false;
      }

      this.resetStock();
      return true;
    }

    // Draw cards based on draw mode
    const drawCount = Math.min(this.drawMode, this.stock.length);
    const drawnCards: Card[] = [];

    for (let i = 0; i < drawCount; i++) {
      const card = this.stock.pop()!;
      card.flip();
      drawnCards.push(card);
    }

    // Add to waste (in reverse order so the last drawn is on top)
    this.waste.push(...drawnCards.reverse());

    // Record the move
    this.history.push({
      fromPile: PileType.STOCK,
      fromIndex: 0,
      toPile: PileType.WASTE,
      toIndex: 0,
      cards: drawnCards.map(c => c.toData()),
      timestamp: Date.now()
    });

    return true;
  }

  /**
   * Reset stock from waste when stock is empty
   */
  private resetStock(): void {
    // Move all cards from waste back to stock
    while (this.waste.length > 0) {
      const card = this.waste.pop()!;
      card.flipDown();
      this.stock.push(card);
    }

    // Record the move
    this.history.push({
      fromPile: PileType.WASTE,
      fromIndex: 0,
      toPile: PileType.STOCK,
      toIndex: 0,
      cards: this.stock.map(c => c.toData()),
      timestamp: Date.now()
    });
  }

  /**
   * Move cards between piles
   */
  moveCards(
    fromPile: PileType,
    fromIndex: number,
    toPile: PileType,
    toIndex: number,
    cardCount: number = 1
  ): boolean {
    const sourcePile = this.getPile(fromPile, fromIndex);
    const targetPile = this.getPile(toPile, toIndex);

    if (!sourcePile || !targetPile) {
      return false;
    }

    // Get the cards to move
    const cardsToMove = sourcePile.slice(-cardCount);

    if (cardsToMove.length === 0) {
      return false;
    }

    // Validate the move
    if (!GameRules.isValidMove(cardsToMove, fromPile, sourcePile, toPile, targetPile)) {
      return false;
    }

    // Execute the move
    sourcePile.splice(-cardCount, cardCount);
    targetPile.push(...cardsToMove);

    // Check for auto-flip
    let flippedCard: Card | null = null;
    if (fromPile === PileType.TABLEAU) {
      flippedCard = GameRules.getAutoFlipCard(sourcePile);
      if (flippedCard) {
        flippedCard.flip();
      }
    }

    // Record the move
    this.history.push({
      fromPile,
      fromIndex,
      toPile,
      toIndex,
      cards: cardsToMove.map(c => c.toData()),
      flippedCard: flippedCard?.id,
      timestamp: Date.now()
    });

    return true;
  }

  /**
   * Undo the last move
   */
  undo(): boolean {
    const move = this.history.undo();
    if (!move) {
      return false;
    }

    return this.reverseMove(move);
  }

  /**
   * Redo the last undone move
   */
  redo(): boolean {
    const move = this.history.redo();
    if (!move) {
      return false;
    }

    return this.applyMove(move);
  }

  /**
   * Apply a move (for redo)
   */
  private applyMove(move: Move): boolean {
    const sourcePile = this.getPile(move.fromPile, move.fromIndex);
    const targetPile = this.getPile(move.toPile, move.toIndex);

    if (!sourcePile || !targetPile) {
      return false;
    }

    // Special case: stock to waste or waste to stock
    if (move.fromPile === PileType.STOCK && move.toPile === PileType.WASTE) {
      return this.drawFromStock();
    }

    if (move.fromPile === PileType.WASTE && move.toPile === PileType.STOCK) {
      this.resetStock();
      return true;
    }

    // Move the cards
    const cards = move.cards.map(data => Card.fromData(data));
    sourcePile.splice(-cards.length, cards.length);
    targetPile.push(...cards);

    // Handle flipped card
    if (move.flippedCard) {
      const cardToFlip = sourcePile.find(c => c.id === move.flippedCard);
      if (cardToFlip) {
        cardToFlip.flip();
      }
    }

    return true;
  }

  /**
   * Reverse a move (for undo)
   */
  private reverseMove(move: Move): boolean {
    const sourcePile = this.getPile(move.fromPile, move.fromIndex);
    const targetPile = this.getPile(move.toPile, move.toIndex);

    if (!sourcePile || !targetPile) {
      return false;
    }

    // Special case: stock to waste or waste to stock
    if (move.fromPile === PileType.STOCK && move.toPile === PileType.WASTE) {
      // Reverse draw: move cards from waste back to stock
      const cards = move.cards.map(data => Card.fromData(data));
      for (let i = 0; i < cards.length; i++) {
        const card = this.waste.pop();
        if (card) {
          card.flipDown();
          this.stock.push(card);
        }
      }
      return true;
    }

    if (move.fromPile === PileType.WASTE && move.toPile === PileType.STOCK) {
      // Reverse reset: move cards from stock back to waste
      while (this.stock.length > 0) {
        const card = this.stock.pop()!;
        card.flip();
        this.waste.push(card);
      }
      return true;
    }

    // Handle flipped card (unflip it)
    if (move.flippedCard) {
      const cardToUnflip = sourcePile.find(c => c.id === move.flippedCard);
      if (cardToUnflip) {
        cardToUnflip.flipDown();
      }
    }

    // Move the cards back
    const cards = targetPile.splice(-move.cards.length, move.cards.length);
    sourcePile.push(...cards);

    return true;
  }

  /**
   * Get a pile by type and index
   */
  private getPile(pileType: PileType, index: number): Card[] | null {
    switch (pileType) {
      case PileType.STOCK:
        return this.stock;
      case PileType.WASTE:
        return this.waste;
      case PileType.FOUNDATION:
        return this.foundations[index] || null;
      case PileType.TABLEAU:
        return this.tableau[index] || null;
      default:
        return null;
    }
  }

  /**
   * Check if the game is won
   */
  isWon(): boolean {
    return GameRules.isGameWon(this.foundations);
  }

  /**
   * Check if auto-complete is available
   */
  canAutoComplete(): boolean {
    return GameRules.canAutoComplete(this.tableau);
  }

  /**
   * Get current game state data (for UI rendering)
   */
  getState(): GameStateData {
    return {
      stock: this.stock.map(c => c.toData()),
      waste: this.waste.map(c => c.toData()),
      foundations: this.foundations.map(f => f.map(c => c.toData())),
      tableau: this.tableau.map(t => t.map(c => c.toData())),
      drawMode: this.drawMode,
      moveCount: this.history.getMoveCount(),
      startTime: this.startTime,
      moves: this.history.getPastMoves()
    };
  }

  /**
   * Get piles for direct access (use carefully!)
   */
  getStock(): Card[] { return this.stock; }
  getWaste(): Card[] { return this.waste; }
  getFoundations(): Card[][] { return this.foundations; }
  getTableau(): Card[][] { return this.tableau; }

  canUndo(): boolean { return this.history.canUndo(); }
  canRedo(): boolean { return this.history.canRedo(); }

  setDrawMode(mode: DrawMode): void {
    this.drawMode = mode;
  }

  getDrawMode(): DrawMode {
    return this.drawMode;
  }

  setSolvableOnly(value: boolean): void {
    this.solvableOnly = value;
  }

  getSolvableOnly(): boolean {
    return this.solvableOnly;
  }

  /**
   * Serialize to JSON for localStorage
   */
  serialize(): string {
    return JSON.stringify(this.getState());
  }

  /**
   * Deserialize from JSON
   */
  static deserialize(json: string, drawMode: DrawMode = DrawMode.THREE_CARD): GameState {
    const data: GameStateData = JSON.parse(json);
    const state = new GameState(drawMode);

    state.stock = data.stock.map(c => Card.fromData(c));
    state.waste = data.waste.map(c => Card.fromData(c));
    state.foundations = data.foundations.map(f => f.map(c => Card.fromData(c)));
    state.tableau = data.tableau.map(t => t.map(c => Card.fromData(c)));
    state.drawMode = data.drawMode;
    state.startTime = data.startTime;

    // Restore move history (handle old data without moves)
    if (data.moves) {
      state.history.restore(data.moves);
    }

    return state;
  }
}
